import React, { useEffect, useState } from 'react';
import { Box, Typography, Button, Chip, Fade } from '@mui/material';
import Lottie from 'react-lottie';
import AIAnimation from '../../assets/lottie/ai-v2.json';
import { useLocation, useNavigate } from 'react-router-dom';
import { getRelativeFileUrl } from '../../services/relativeFilesService';

interface TableOfContentsItem {
  title: string;
  page: number;
}

interface PublicationCategory {
  id: number | string;
  category: string;
}

interface PublicationData {
  publication_id: string;
  pdf_path?: string;
  title?: string;
  resume?: string;
  table_of_contents?: TableOfContentsItem[];
  categories?: PublicationCategory[];
  is_for_dom?: boolean;
  is_for_dou?: boolean;
  is_for_jgc?: boolean;
}

const PublicationResult: React.FC = () => {
  const location = useLocation();
  const navigate = useNavigate();

  const { publication } = (location.state || {}) as {
    publication?: PublicationData;
  };

  // Hooks
  const [showAnimation, setShowAnimation] = useState(true);
  const [showTitle, setShowTitle] = useState(false);
  const [showResume, setShowResume] = useState(false);
  const [showCategories, setShowCategories] = useState(false);
  const [showDestination, setShowDestination] = useState(false);
  const [showTOC, setShowTOC] = useState(false);

  // Montar sumário
  const sumarioString = (publication?.table_of_contents || [])
    .map((item) => `• ${item.title} (pág. ${item.page})`)
    .join('\n');

  // Cores para as categorias
  const categoryColors = ['primary', 'secondary', 'success', 'error', 'warning', 'info'];

  // Cores fixas para o “Onde?”
  const destinoColors = {
    dom: 'info',
    dou: 'warning',
    jgc: 'error',
  };

  // Animação Lottie
  const defaultOptions = {
    loop: true,
    autoplay: true,
    animationData: AIAnimation,
    rendererSettings: {
      preserveAspectRatio: 'xMidYMid slice',
    },
  };

  // Efeito de exibir seções em sequência
  useEffect(() => {
    const timer1 = setTimeout(() => setShowTitle(true), 1000);
    const timer2 = setTimeout(() => setShowResume(true), 2000);
    const timer3 = setTimeout(() => setShowCategories(true), 3000);
    const timer4 = setTimeout(() => setShowDestination(true), 4000);
    const timer5 = setTimeout(() => setShowTOC(true), 5000);
    const timer6 = setTimeout(() => setShowAnimation(false), 6000);

    return () => {
      clearTimeout(timer1);
      clearTimeout(timer2);
      clearTimeout(timer3);
      clearTimeout(timer4);
      clearTimeout(timer5);
      clearTimeout(timer6);
    };
  }, []);

  // Se não temos publication
  if (!publication) {
    return (
      <Box sx={{ p: 4 }}>
        <Typography>Não há informações de publicação para exibir.</Typography>
        <Button variant="contained" sx={{ mt: 2 }} onClick={() => navigate('/dashboard')}>
          Voltar à Dashboard
        </Button>
      </Box>
    );
  }

  return (
    <Box
      sx={{
        mt: 3,
        width: '100%',
        px: 3,
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'flex-start',
      }}
    >
      {/* Animação Lottie no topo */}
      {showAnimation && (
        <Box sx={{ width: 180, mb: 2, alignSelf: 'center' }}>
          <Lottie options={defaultOptions} />
        </Box>
      )}

      {/* TÍTULO */}
      <Fade in={showTitle} timeout={600}>
        <Box sx={{ mb: 2 }}>
          <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
            {publication.title || 'Título não fornecido'}
          </Typography>
        </Box>
      </Fade>

      {/* RESUMO */}
      <Fade in={showResume} timeout={600}>
        <Box sx={{ mb: 2 }}>
          <Typography variant="body1" sx={{ whiteSpace: 'pre-line' }}>
            {publication.resume || 'Resumo não fornecido'}
          </Typography>
        </Box>
      </Fade>

      {/* CATEGORIAS */}
      <Fade in={showCategories} timeout={600}>
        <Box sx={{ mb: 2, display: 'flex', gap: 1, flexWrap: 'wrap' }}>
          {(publication.categories || []).map((cat, idx) => (
            <Chip
              key={cat.id}
              label={cat.category}
              color={categoryColors[idx % categoryColors.length] as any}
              variant="outlined"
            />
          ))}
        </Box>
      </Fade>

      {/* ONDE / DESTINO */}
      <Fade in={showDestination} timeout={600}>
        <Box sx={{ mb: 2, display: 'flex', gap: 1, flexWrap: 'wrap' }}>
          {publication.is_for_dom && (
            <Chip
              label="Diário Oficial (próprio)"
              color={destinoColors.dom as any}
              variant="outlined"
            />
          )}
          {publication.is_for_dou && (
            <Chip
              label="Diário Oficial da União"
              color={destinoColors.dou as any}
              variant="outlined"
            />
          )}
          {publication.is_for_jgc && (
            <Chip
              label="Jornal de Grande Circulação"
              color={destinoColors.jgc as any}
              variant="outlined"
            />
          )}
        </Box>
      </Fade>

      {/* SUMÁRIO */}
      <Fade in={showTOC} timeout={600}>
        <Box sx={{ mb: 3 }}>
          {sumarioString && (
            <>
              <Typography variant="subtitle1" sx={{ fontWeight: 'bold', mb: 1 }}>
                Sumário:
              </Typography>
              <Typography variant="body2" sx={{ whiteSpace: 'pre-line' }}>
                {sumarioString}
              </Typography>
            </>
          )}
        </Box>
      </Fade>

      {/* BOTOES FINAIS */}
      {!showAnimation && (
        <Box sx={{ mb: 3 }}>
          {publication.pdf_path && (
            <Button
              variant="outlined"
              sx={{ mb: 2 }}
              onClick={() => {
                const pdfUrl = getRelativeFileUrl(publication.pdf_path);
                window.open(pdfUrl, '_blank');
              }}
            >
              Acessar PDF Gerado
            </Button>
          )}
          <Typography variant="body2" sx={{ mb: 2 }}>
            Publicação ID: {publication.publication_id}
          </Typography>

          <Box sx={{ display: 'flex', gap: 2 }}>
            <Button variant="contained" onClick={() => navigate('/dashboard')}>
              Ir para a Dashboard
            </Button>
            <Button variant="outlined" onClick={() => navigate('/publicacoes/nova')}>
              Criar outra Publicação
            </Button>
          </Box>
        </Box>
      )}
    </Box>
  );
};

export default PublicationResult;